import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CustomizablePdfDeal } from '@makemydeal/dr-platform-types';
import type { DealGridSelection } from '@makemydeal/dr-services-pdf-shared';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

import {
    getDealProposal,
    getDealProposalFetchStatus,
    getDeals,
    getFuelSavingData,
    getDefaultActiveScenario,
    getLayoutType,
    getPrintConfig,
    getVehicle,
    requestProposalSelectors
} from '../store';
import type { DealProposalsView } from '../types';

export const useDealProposals = (): { dealProposals: DealProposalsView } => {
    const dispatch = useDispatch();
    const [selection, setSelection] = useState<{ scenarios: CustomizablePdfDeal[]; cell: DealGridSelection }>();

    const publishedVersion = useSelector(requestProposalSelectors.additionalSelectors.getPublishedVersion);
    const requestStatus = useSelector(getDealProposalFetchStatus);
    const layoutType = useSelector(getLayoutType);
    const vehicle = useSelector(getVehicle);
    const fuelSavingData = useSelector(getFuelSavingData);
    const scenarios = useSelector(getDeals);
    const defaultActiveScenario = useSelector(getDefaultActiveScenario);
    const printConfig = useSelector(getPrintConfig);
    const salesPersonName = useSelector(requestProposalSelectors.getSalesPersonInfo);
    const customerInfo = useSelector(requestProposalSelectors.getCustomerInfo);
    const trades = useSelector(requestProposalSelectors.getTradesInfo);

    useEffect(() => {
        if (publishedVersion && requestStatus === ApiRequestStatus.Idle) {
            dispatch(getDealProposal());
        }
    }, [dispatch, publishedVersion, requestStatus]);

    const onGridCellChange = useCallback((cell: DealGridSelection) => setSelection({ scenarios, cell }), [scenarios]);

    const activeSelection = selection?.scenarios === scenarios ? selection.cell : undefined;
    const activeScenario = activeSelection ? scenarios[activeSelection.dealIndex] : defaultActiveScenario;

    const dealProposals = useMemo(
        () => ({
            requestStatus,
            layoutType,
            salesPersonName,
            customerInfo,
            vehicle,
            fuelSavingData,
            trades,
            scenarios,
            activeScenario,
            activeSelection,
            onGridCellChange,
            printConfig
        }),
        [
            requestStatus,
            layoutType,
            salesPersonName,
            customerInfo,
            vehicle,
            fuelSavingData,
            trades,
            scenarios,
            activeScenario,
            activeSelection,
            onGridCellChange,
            printConfig
        ]
    );

    return { dealProposals };
};
